import { Controller } from "@hotwired/stimulus"

// Manual square headshot cropper (uses the globally-loaded Cropper.js v1, see
// admin layout). The admin drags a 1:1 box over the face; the crop region is
// written to hidden fields as FRACTIONS (0–1) of the image's natural size and
// applied server-side via an ActiveStorage variant. Storing fractions keeps the
// crop aligned even after the original is downscaled on upload. Non-destructive:
// the original upload is kept.
//
// Final values are captured from the cropper's settled state on form submit, so
// a slow-loading (e.g. S3) image can't leave a transient zero-size crop stored.
export default class extends Controller {
  static targets = ["image", "preview", "x", "y", "width", "height", "placeholder"]

  connect() {
    if (this.hasImageTarget && this.imageTarget.getAttribute("src")) {
      this.initCropper()
    }
    this.form = this.element.closest("form")
    if (this.form) {
      this.boundCapture = this.captureCrop.bind(this)
      this.form.addEventListener("submit", this.boundCapture)
    }
  }

  disconnect() {
    this.destroyCropper()
    if (this.form && this.boundCapture) {
      this.form.removeEventListener("submit", this.boundCapture)
    }
  }

  // Triggered when the admin picks a new file in the file input.
  fileSelected(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this.destroyCropper()
      this.clearCrop() // coords are in the new image's pixel space
      this.imageTarget.src = e.target.result
      this.showCropper()
      this.initCropper()
    }
    reader.readAsDataURL(file)
  }

  initCropper() {
    this.showCropper()
    const saved = this.savedCrop()
    this.cropper = new Cropper(this.imageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      checkCrossOrigin: false, // image loads fine for display; we never read canvas pixels
      preview: this.hasPreviewTarget ? this.previewTarget : undefined,
      ready: () => {
        if (saved) {
          const img = this.cropper.getImageData()
          this.cropper.setData({
            x: saved.x * img.naturalWidth,
            y: saved.y * img.naturalHeight,
            width: saved.width * img.naturalWidth,
            height: saved.height * img.naturalHeight,
          })
        }
        this.writeFractions() // seed hidden fields from the initial crop box
      },
      crop: () => this.writeFractions(),
    })
  }

  // Write the cropper's current selection to the hidden fields as fractions.
  // Ignores degenerate boxes (e.g. before the image has real dimensions).
  writeFractions() {
    if (!this.cropper) return
    const img = this.cropper.getImageData()
    const data = this.cropper.getData()
    if (!img.naturalWidth || !img.naturalHeight) return
    if (!(data.width > 0) || !(data.height > 0)) return

    this.xTarget.value = data.x / img.naturalWidth
    this.yTarget.value = data.y / img.naturalHeight
    this.widthTarget.value = data.width / img.naturalWidth
    this.heightTarget.value = data.height / img.naturalHeight
  }

  // Capture the final, settled crop on submit (authoritative).
  captureCrop() {
    this.writeFractions()
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  showCropper() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
    this.imageTarget.classList.remove("hidden")
  }

  // Returns previously-saved crop data as fractions (0–1) or null.
  savedCrop() {
    const width = parseFloat(this.widthTarget.value)
    const height = parseFloat(this.heightTarget.value)
    if (!width || !height) return null
    return {
      x: parseFloat(this.xTarget.value) || 0,
      y: parseFloat(this.yTarget.value) || 0,
      width,
      height,
    }
  }

  clearCrop() {
    this.xTarget.value = ""
    this.yTarget.value = ""
    this.widthTarget.value = ""
    this.heightTarget.value = ""
  }
}
